import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Activity, Users, Eye, TrendingUp, Loader2, RefreshCw } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const fmtRelative = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return `${Math.max(1, Math.floor(diff / 1000))}s`;
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3600_000)}h`;
  return `${Math.floor(diff / 86_400_000)}d`;
};

const Kpi = ({ label, value, icon: Icon, accent }) => (
  <div className="border border-white/10 bg-[#121214] p-5">
    <div className="flex items-center justify-between mb-3">
      <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <Icon className={`h-4 w-4 ${accent || "text-zinc-500"}`} />
    </div>
    <div className="font-heading text-3xl font-bold tracking-tighter">{value}</div>
  </div>
);

const TopList = ({ title, items, keyLabel = "Item" }) => (
  <div className="border border-white/10 bg-[#121214] p-5">
    <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4">{title}</div>
    <table className="w-full text-sm">
      <thead>
        <tr className="text-xs font-mono uppercase text-zinc-600 border-b border-white/5">
          <th className="text-left pb-2">{keyLabel}</th>
          <th className="text-right pb-2">Visitantes</th>
        </tr>
      </thead>
      <tbody>
        {items.length === 0 && <tr><td colSpan={2} className="py-3 text-zinc-600 text-xs">sem dados</td></tr>}
        {items.map((it, i) => (
          <tr key={`${it.key}-${i}`} className="border-b border-white/5 last:border-0">
            <td className="py-2 font-mono text-xs text-zinc-300 truncate max-w-[240px]" title={it.key}>{it.key}</td>
            <td className="py-2 font-mono text-xs text-right">{it.count}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function AdminAnalytics() {
  const [stats, setStats] = useState(null);
  const [live, setLive] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/visits/stats"),
        api.get("/admin/visits/live?limit=60"),
      ]);
      setStats(s.data);
      setLive(l.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(() => load(true), 15_000); // auto refresh a cada 15s
    return () => clearInterval(iv);
  }, []);

  if (loading || !stats) {
    return <div className="text-zinc-500 font-mono text-xs">Carregando analytics…</div>;
  }

  const t = stats.totals;

  return (
    <div className="space-y-6" data-testid="admin-analytics-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">// admin · analytics</div>
          <h1 className="font-heading text-3xl font-bold tracking-tighter">Visitantes do site</h1>
          <p className="text-xs text-zinc-500 mt-1">Auto-refresh a cada 15s · bots filtrados · 1 visitante = 1 IP</p>
        </div>
        <Button onClick={() => load(true)} disabled={refreshing} variant="outline" className="rounded-sm border-white/20 hover:bg-white hover:text-black">
          {refreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Atualizar
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Ativos (5 min)" value={t.active_now} icon={Activity} accent="text-emerald-400" />
        <Kpi label="Visitantes hoje" value={t.today} icon={Eye} accent="text-pink-400" />
        <Kpi label="Únicos hoje" value={t.unique_today} icon={Users} accent="text-purple-400" />
        <Kpi label="Visitantes 7 dias" value={t.seven_days} icon={TrendingUp} accent="text-blue-400" />
      </div>

      {/* Chart */}
      <div className="border border-white/10 bg-[#121214] p-5">
        <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4">// visitas últimos 14 dias</div>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <LineChart data={stats.series_14d} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="grad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#a855f7" />
                  <stop offset="100%" stopColor="#ec4899" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="day" tickFormatter={(d) => d.slice(5)} stroke="#71717a" fontSize={11} tick={{ fill: "#71717a" }} />
              <YAxis stroke="#71717a" fontSize={11} tick={{ fill: "#71717a" }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#09090B", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 0, fontFamily: "monospace", fontSize: 12 }} labelStyle={{ color: "#a1a1aa" }} />
              <Line type="monotone" dataKey="count" stroke="url(#grad)" strokeWidth={2.5} dot={{ r: 3, fill: "#ec4899" }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top lists */}
      <div className="grid md:grid-cols-3 gap-4">
        <TopList title="// páginas mais visitadas · 7d" items={stats.top_paths_7d} keyLabel="Path" />
        <TopList title="// referrers · 7d" items={stats.top_referrers_7d} keyLabel="Origem" />
        <TopList title="// navegadores · 7d" items={stats.top_browsers_7d} keyLabel="Browser" />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <TopList title="// sistema operacional · 7d" items={stats.top_os_7d} keyLabel="OS" />
        <TopList title="// dispositivo · 7d" items={stats.top_devices_7d} keyLabel="Tipo" />
      </div>

      {/* Live feed */}
      <div className="border border-white/10 bg-[#121214]">
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">// feed ao vivo · últimas 60 visitas</div>
          <div className="flex items-center gap-2 text-xs font-mono text-emerald-400">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> AO VIVO
          </div>
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="text-xs font-mono uppercase text-zinc-600 sticky top-0 bg-[#121214]">
              <tr className="border-b border-white/10">
                <th className="text-left px-5 py-2">Quando</th>
                <th className="text-left px-5 py-2">Path</th>
                <th className="text-left px-5 py-2">IP</th>
                <th className="text-left px-5 py-2">Browser</th>
                <th className="text-left px-5 py-2">OS</th>
                <th className="text-left px-5 py-2">Ref</th>
              </tr>
            </thead>
            <tbody>
              {live.map((v, i) => (
                <tr key={i} className={`border-b border-white/5 hover:bg-white/5 ${v.is_bot ? "opacity-40" : ""}`}>
                  <td className="px-5 py-2 font-mono text-xs text-zinc-500">{fmtRelative(v.created_at)} atrás</td>
                  <td className="px-5 py-2 font-mono text-xs truncate max-w-[200px]" title={v.path}>{v.path}</td>
                  <td className="px-5 py-2 font-mono text-xs text-zinc-500">{v.ip}</td>
                  <td className="px-5 py-2 font-mono text-xs">{v.browser}{v.is_bot && <span className="ml-2 text-amber-400">[BOT]</span>}</td>
                  <td className="px-5 py-2 font-mono text-xs">{v.os}</td>
                  <td className="px-5 py-2 font-mono text-xs text-zinc-500 truncate max-w-[180px]" title={v.referrer}>{v.referrer || "—"}</td>
                </tr>
              ))}
              {live.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-zinc-500 text-xs font-mono">nenhuma visita ainda</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
